// @ts-nocheck
import axios from "axios";
import { getAccessTokenFromAuth, persistAccessToken } from "../auth/authClient";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BASE_URL || "";
const USE_MOCKS =
  String(process.env.REACT_APP_USE_MOCKS).toLowerCase() === "true";

async function getTraceId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}


export async function requestStream(path, options = {}) {
  const {
    method = "POST",
    body,
    auth: needsAuth = false,
    getAccessToken,
    onEvent,
  } = options;

  const headers = { "Content-Type": "application/json" };

  if (needsAuth) {
    let token = await getAccessTokenFromAuth();
    if (!token && typeof getAccessToken === "function") {
      const fetchedToken = await getAccessToken();
      token = fetchedToken ? persistAccessToken(fetchedToken) : null;
    }
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const traceId = await getTraceId();
  headers["X-Trace-Id"] = traceId;

  const url = API_BASE_URL ? `${API_BASE_URL}${path}` : path;

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    throw new Error(`Stream request failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const parsed = JSON.parse(line.slice(6));
          if (onEvent) onEvent(parsed);
        } catch (_e) {
          // skip malformed SSE lines
        }
      }
    }
  }

  // Process any remaining buffer
  if (buffer.startsWith("data: ")) {
    try {
      const parsed = JSON.parse(buffer.slice(6));
      if (onEvent) onEvent(parsed);
    } catch (_e) {
      // skip
    }
  }
}

export async function request(path, options = {}) {
  const {
    method = "GET",
    body,
    auth = false,
    getAccessToken,
    headers: extraHeaders = {},
    signal,
    timeout = 60000,
  } = options;

  const headers = { ...extraHeaders };
  if (body && !headers["Content-Type"])
    headers["Content-Type"] = "application/json";

  if (auth) {
    let token = await getAccessTokenFromAuth();
    if (!token && typeof getAccessToken === "function") {
      const fetchedToken = await getAccessToken();
      token = fetchedToken ? persistAccessToken(fetchedToken) : null;
    }
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const traceId = await getTraceId();
  headers["X-Trace-Id"] = headers["X-Trace-Id"] || traceId;

  if (USE_MOCKS) {
    const { mockRequest } = await import("../mocks");
    const envelope = await mockRequest(path, { method, body, headers });
    if (envelope && typeof envelope === "object" && "status" in envelope) {
      if (envelope.status === "success") return envelope.data;
      const err = new Error(envelope.error?.message || "Request failed");
      if (envelope.error && envelope.error.code)
        err.errorCode = envelope.error.code;
      throw err;
    }
    return envelope;
  }

  const url = API_BASE_URL ? `${API_BASE_URL}${path}` : path;
  const res = await axios({
    url,
    method,
    headers,
    data: body ? body : undefined,
    signal,
    timeout,
    validateStatus: () => true,
  });

  const json = res.data;

  if (res.status < 200 || res.status >= 300) {
    const msg =
      (json && (json.error?.message || json.error || json.message)) ||
      `Request failed: ${res.status}`;
    const err = new Error(msg);
    err.httpStatus = res.status;
    if (json && json.error && json.error.code) err.errorCode = json.error.code;
    throw err;
  }

  if (json && typeof json === "object" && "status" in json) {
    if (json.status === "success") return json.data;
    const err = new Error(json.error?.message || "Request failed");
    if (json.error && json.error.code) err.errorCode = json.error.code;
    throw err;
  }
  return json;
}
